import { MessageOutput } from '@quadminds/client-apis/agents';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Attachment } from '../components/pages/Chat/Chat/types';
import { useChatContext } from '../providers/Chat/Chat.provider';
import ChatService from '../services/chat.service';
import { isCancelledRequestError } from '../utils/agentsRequest';

type KnowledgeMessageOutput = MessageOutput & { request_id: string };

export function useSendKnowledgeMessage(
    optimisticRequest?: (message: string) => void,
    callbackResponse?: ({
        message,
        attachments,
        interactionId,
        conversationId,
        rating,
    }: {
        message: string;
        attachments?: Attachment[];
        interactionId?: string;
        conversationId?: string;
        rating?: string | null;
    }) => void,
    finallyCallback?: () => void,
) {
    const chatService = useMemo(() => new ChatService(), []);
    const queryClient = useQueryClient();
    const { setConversationId, conversationId } = useChatContext();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const activeRequestIdRef = useRef<string | null>(null);
    const activeRequestAbortControllerRef = useRef<AbortController | null>(null);
    const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
    const cancelledRequestIdsRef = useRef<Set<string>>(new Set());
    const lastKnownConversationIdRef = useRef<string | null>(conversationId ?? null);

    const errorsActions = {
        REQUEST_LIMIT_REACHED: '/error/limit-exceeded',
        GEOCODE_LIMIT_REACHED: '/error/geo-limit',
    };

    const mutation = useMutation({
        mutationFn: (message: KnowledgeMessageOutput) =>
            chatService.sendKnowledgeMessage(
                message,
                message.request_id,
                activeRequestAbortControllerRef.current?.signal,
            ),
        onMutate(message: KnowledgeMessageOutput) {
            activeRequestAbortControllerRef.current = new AbortController();
            activeRequestIdRef.current = message.request_id;
            setActiveRequestId(message.request_id);
            // Handle optimistic update for user message
            if (optimisticRequest) optimisticRequest(message.query);
        },
        onSuccess(data, variables, _context) {
            if (cancelledRequestIdsRef.current.has(variables.request_id)) {
                return;
            }
            lastKnownConversationIdRef.current = data.conversation_id;
            // Set conversation ID for new conversations
            if (conversationId === undefined) {
                setConversationId(data.conversation_id);
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: ['conversations'] });
                }, 10000);
            }

            // Handle agent response callback
            if (callbackResponse) {
                callbackResponse({
                    message: data.agent_response,
                    attachments: data?.attachments || undefined,
                    interactionId: data.interaction_id,
                    conversationId: data.conversation_id,
                    rating: null,
                });
            }
        },
        onError(error, variables, _context) {
            if (cancelledRequestIdsRef.current.has(variables.request_id)) {
                return;
            }
            if (isCancelledRequestError(error)) {
                return;
            }
            if (error && (error as Error & { statusCode: string })?.statusCode) {
                const statusCode = (error as Error & { statusCode: string }).statusCode;
                if (Object.keys(errorsActions).includes(statusCode)) {
                    navigate(errorsActions[statusCode as keyof typeof errorsActions]);
                }
            }
            if (callbackResponse) callbackResponse({ message: t('errors.mindyGenericErrorResponse') });
            console.error('Error', error);
        },
        onSettled(_data, _error, variables) {
            if (activeRequestIdRef.current === variables?.request_id) {
                activeRequestIdRef.current = null;
            }
            if (activeRequestId === variables?.request_id) {
                setActiveRequestId(null);
            }
            activeRequestAbortControllerRef.current = null;
            if (variables?.request_id && cancelledRequestIdsRef.current.has(variables.request_id)) {
                cancelledRequestIdsRef.current.delete(variables.request_id);
                return;
            }
            if (finallyCallback) finallyCallback();
        },
    });

    const cancelKnowledgeRequest = useCallback(
        async (requestId?: string) => {
            const requestToCancel = requestId ?? activeRequestIdRef.current ?? activeRequestId;
            if (!requestToCancel) return;
            cancelledRequestIdsRef.current.add(requestToCancel);
            activeRequestAbortControllerRef.current?.abort();
            activeRequestAbortControllerRef.current = null;
            activeRequestIdRef.current = null;
            setActiveRequestId(null);

            try {
                const cancelResponse = await chatService.cancelKnowledgeMessage(requestToCancel);
                const conversationIdToInvalidate =
                    cancelResponse.conversation_id || conversationId || lastKnownConversationIdRef.current;

                if (cancelResponse.conversation_id) {
                    lastKnownConversationIdRef.current = cancelResponse.conversation_id;
                }

                if (conversationIdToInvalidate) {
                    queryClient.invalidateQueries({ queryKey: ['conversation', conversationIdToInvalidate] });
                }
            } catch (error) {
                console.error('Error cancelling knowledge request:', error);
            } finally {
                if (activeRequestIdRef.current === requestToCancel) {
                    activeRequestIdRef.current = null;
                }
                mutation.reset();
            }
        },
        [activeRequestId, chatService, conversationId, mutation, queryClient],
    );

    return {
        ...mutation,
        activeRequestId,
        cancelKnowledgeRequest,
    };
}
